import { NodeSubType } from "../../../models/nodeSubType";
import { AttachedAgentAnalytics } from "../../../components/template-agent-attachment/models";
import { AnalyticsToPercentageConverter } from "../../../services/helpers/AnalyticsHelper";
import {
  truncateValueForIndianStore,
  truncateValueForInternationalStore,
} from "@bikdotai/bik-component-library";

export const calculatePercentage = (numerator: number, denominator: number) => {
  if (denominator === 0) {
    return "--";
  }
  const percentage = (numerator / denominator) * 100;
  if (percentage > 100) {
    return "100%";
  } else if (percentage > 0) {
    return `${AnalyticsToPercentageConverter(numerator, denominator)}%`;
  } else {
    return "--";
  }
};

export const getTruncatedRevenue = (
  revenue: number,
  currency: string,
  currencyThreeLetterCode: string
) => {
  const truncatedRevenue =
    currencyThreeLetterCode === "INR"
      ? truncateValueForIndianStore(revenue)
      : truncateValueForInternationalStore(revenue);
  return revenue > 0 ? `${currency} ${truncatedRevenue}` : "--";
};

const getRawMetricText = (value?: number) => {
  if (typeof value !== "number") {
    return undefined;
  }

  return value.toString();
};

const getSuccessText = (executed?: number, triggered?: number) => {
  if (typeof executed !== "number" || typeof triggered !== "number") {
    return undefined;
  }

  if (triggered <= 0) {
    return "--";
  }

  if ((executed / triggered) * 100 > 100) {
    return "100%";
  }

  if ((executed / triggered) * 100 > 0) {
    return `${AnalyticsToPercentageConverter(executed, triggered)}%`;
  }

  return "--";
};

export const getFallbackMessageAnalytics = (
  nodeAnalytics: {
    triggered_count?: number;
    sent_count?: number;
    delivered_count?: number;
    read_count?: number;
    answered_count?: number;
    clicked_count?: number;
    revenue?: number;
    executed_count?: number;
  },
  currency: string,
  currencyThreeLetterCode: string
) => {
  const analytics: { [key: string]: string } = {};
  const triggered = nodeAnalytics?.triggered_count;
  const sent = nodeAnalytics?.sent_count;
  const delivered = nodeAnalytics?.delivered_count;
  const read = nodeAnalytics?.read_count;
  const answered = nodeAnalytics?.answered_count;
  const clicked = nodeAnalytics?.clicked_count;
  const revenue = nodeAnalytics?.revenue;
  const executed = nodeAnalytics?.executed_count;

  if (triggered !== undefined) {
    analytics.Triggered = getRawMetricText(triggered) ?? "--";
  }

  if (sent !== undefined) {
    analytics.Sent = getRawMetricText(sent) ?? "--";
  }

  if (delivered !== undefined) {
    analytics.Delivered = getRawMetricText(delivered) ?? "--";
  }

  if (read !== undefined) {
    analytics.Opened = getRawMetricText(read) ?? "--";
  }

  if (answered !== undefined) {
    analytics.Answered = getRawMetricText(answered) ?? "--";
  }

  if (clicked !== undefined) {
    analytics.Clicked = getRawMetricText(clicked) ?? "--";
  }

  if (revenue !== undefined) {
    analytics.Revenue = getTruncatedRevenue(
      revenue,
      currency,
      currencyThreeLetterCode
    );
  }

  if (executed !== undefined) {
    analytics.Success = getSuccessText(executed, triggered) ?? "--";
  }

  return analytics;
};

export const getEmptyMessageAnalytics = (
  subType: string,
  channel?: string
): { [key: string]: string } => {
  if (channel === "instagram") {
    if (
      subType === "ask_name" ||
      subType === "ask_email" ||
      subType === "ask_phone" ||
      subType === "question" ||
      subType === "CSAT/NPS"
    ) {
      return {
        Triggered: "--",
        Answered: "--",
      };
    }

    return {
      Triggered: "--",
      Sent: "--",
      Opened: "--",
      Revenue: "--",
    };
  }

  switch (subType) {
    case "send_sms":
      return {
        Sent: "--",
        Delivered: "--",
        Clicked: "--",
        Revenue: "--",
      };
    case "email_temp_new":
      return {
        Sent: "--",
        Opened: "--",
        Clicked: "--",
        Revenue: "--",
      };
    case "catalog_list":
    case "order_list":
      return {
        Triggered: "--",
      };
    case "message":
    case "template":
    case "send_rcs":
    case "list":
    case "catalog":
    case "single_product":
    case "multi_product":
    case NodeSubType.WHATSAPP_PRODUCT_CAROUSEL:
    case "cart_info":
    case "carousel":
    case "browse_collection":
    case "wa_carousel":
    case "product_card":
    case NodeSubType.WHATSAPP_PAYMENT:
    case NodeSubType.ORDER_CONFIRMATION:
      return {
        Sent: "--",
        Delivered: "--",
        Opened: "--",
        Revenue: "--",
      };
    case "ask_name":
    case "ask_email":
    case "ask_phone":
    case "question":
    case "CSAT/NPS":
    case NodeSubType.WHATSAPP_ADDRESS:
      return {
        Sent: "--",
        Delivered: "--",
        Opened: "--",
        Answered: "--",
      };
    case "gmail_reply":
      return {
        Triggered: "--",
        Success: "--",
      };
    default:
      return {
        Triggered: "--",
        Sent: "--",
      };
  }
};

export const getMessageAnalytics = (
  subType: string,
  triggered: number,
  executed: number,
  sent: number,
  del: string,
  readPercentage: string,
  answered: number,
  revenue: number,
  clicked: number,
  currency: string,
  currencyThreeLetterCode: string
) => {
  const analytics: { [key: string]: any } = {};

  switch (subType) {
    case "email_temp_new":
      analytics.Sent = sent;
      analytics.Opened = readPercentage;
      analytics.Clicked = clicked ? calculatePercentage(clicked, sent) : "--";
      analytics.Revenue = getTruncatedRevenue(
        revenue,
        currency,
        currencyThreeLetterCode
      );
      break;
    case "catalog_list":
    case "order_list":
      analytics.Triggered = triggered > 0 ? triggered.toString() : "--";
      break;
    case "message":
    case "template":
    case "send_rcs":
    case "list":
    case "catalog":
    case "single_product":
    case "multi_product":
    case NodeSubType.WHATSAPP_PRODUCT_CAROUSEL:
    case "cart_info":
    case "carousel":
    case "browse_collection":
    case "wa_carousel":
    case "product_card":
    case NodeSubType.WHATSAPP_PAYMENT:
    case NodeSubType.ORDER_CONFIRMATION:
      analytics.Sent = sent > 0 ? sent.toString() : "--";
      analytics.Delivered = del;
      analytics.Opened = readPercentage;
      analytics.Revenue = getTruncatedRevenue(
        revenue,
        currency,
        currencyThreeLetterCode
      );
      break;
    case "ask_name":
    case "ask_email":
    case "ask_phone":
    case "question":
    case "CSAT/NPS":
    case NodeSubType.WHATSAPP_ADDRESS:
      analytics.Sent = sent > 0 ? sent.toString() : "--";
      analytics.Delivered = del;
      analytics.Opened = readPercentage;
      analytics.Answered = calculatePercentage(answered, sent);
      break;
    case "gmail_reply":
      analytics.Triggered = triggered > 0 ? triggered.toString() : "--";
      analytics.Success =
        (executed / triggered) * 100 > 100
          ? "100%"
          : (executed / triggered) * 100 > 0
            ? `${AnalyticsToPercentageConverter(executed, triggered)}%`
            : "--";
      break;
  }

  return analytics;
};

// The attached agent's row of a host node's analytics card, before it has numbers.
export const getEmptyAttachedAgentAnalytics = (
  subType: string
): { [key: string]: string } => {
  switch (subType) {
    case NodeSubType.TEMPLATE:
      return {
        "AI Sessions": "--",
        "AI Orders": "--",
        "AI Revenue": "--",
      };
    default:
      return {};
  }
};

// What /get-flow-analytics nests under the host node's `agent` key.
export const getAttachedAgentAnalytics = (
  agent: AttachedAgentAnalytics,
  currency: string,
  currencyThreeLetterCode: string
): { [key: string]: string } => {
  return {
    "AI Sessions": agent.aiSessions > 0 ? agent.aiSessions.toString() : "--",
    "AI Orders": agent.orders > 0 ? agent.orders.toString() : "--",
    "AI Revenue": getTruncatedRevenue(
      agent.revenue,
      currency,
      currencyThreeLetterCode
    ),
  };
};
